import React, { useState, useEffect } from 'react';
import { userAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const UsersTab = ({ pendingUsers = [], loadPendingUsers }) => {
  const [activatingUser, setActivatingUser] = useState(null);
  const [members, setMembers] = useState([]);
  const [activeSection, setActiveSection] = useState('members');
  const [showAddMember, setShowAddMember] = useState(false);
  const [showEditMember, setShowEditMember] = useState(false);
  const [showMemberProfile, setShowMemberProfile] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberForm, setMemberForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: ''
  });

  const loadMembers = async () => {
    try {
      console.log('Loading members...');
      const response = await userAPI.getAllUsers();
      console.log('Members API Response:', response);
      if (response.success) {
        console.log('Members data:', response.users);
        setMembers(response.users || []);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Mitglieder:', error);
      toast.error('Fehler beim Laden der Mitglieder');
    }
  };

  const handleMemberFormChange = (e) => {
    const { name, value } = e.target;
    setMemberForm({
      ...memberForm,
      [name]: value
    });
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      const response = await userAPI.createUser({
        first_name: memberForm.firstName,
        last_name: memberForm.lastName,
        email: memberForm.email,
        username: memberForm.username,
        password: memberForm.password
      });
      
      if (response.success) {
        toast.success('Mitglied wurde erfolgreich hinzugefügt');
        setMemberForm({
          firstName: '',
          lastName: '',
          email: '',
          username: '',
          password: ''
        });
        setShowAddMember(false);
        await loadMembers();
      }
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Mitglieds:', error);
      toast.error('Fehler beim Hinzufügen des Mitglieds');
    }
  };

  const handleActivateUser = async (user) => {
    setActivatingUser(user.id);
    try {
      const response = await userAPI.activateUser(user.id);
      if (response.success) {
        toast.success(
          response.emailSent === false
            ? `${user.first_name} ${user.last_name} ist freigeschaltet. Die Benachrichtigungsmail konnte nicht verschickt werden.`
            : `${user.first_name} ${user.last_name} ist freigeschaltet und wurde per Mail informiert.`
        );
        if (loadPendingUsers) await loadPendingUsers();
        await loadMembers();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Freischalten fehlgeschlagen');
    } finally {
      setActivatingUser(null);
    }
  };

  const handleToggleBlock = async (member) => {
    const sperren = member.status !== 'gesperrt';
    const frage = sperren
      ? `"${member.first_name} ${member.last_name}" sperren? Die Person kann sich dann nicht mehr anmelden. Alle Daten und Anmeldungen bleiben erhalten.`
      : `Sperre für "${member.first_name} ${member.last_name}" wieder aufheben?`;
    if (!window.confirm(frage)) return;

    try {
      const response = await userAPI.blockUser(member.id, sperren);
      if (response.success) {
        toast.success(response.message);
        await loadMembers();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Die Sperre konnte nicht geändert werden');
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setMemberForm({
      firstName: member.first_name,
      lastName: member.last_name,
      email: member.email,
      username: member.username,
      password: '' // Passwort leer lassen für Bearbeitung
    });
    setShowEditMember(true);
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        first_name: memberForm.firstName,
        last_name: memberForm.lastName,
        email: memberForm.email,
        username: memberForm.username
      };

      // Nur Passwort hinzufügen wenn es geändert wurde
      if (memberForm.password) {
        updateData.password = memberForm.password;
      }

      const response = await userAPI.updateUser(selectedMember.id, updateData);
      
      if (response.success) {
        toast.success('Mitglied wurde erfolgreich aktualisiert');
        setShowEditMember(false);
        setSelectedMember(null);
        await loadMembers();
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Mitglieds:', error);
      toast.error('Fehler beim Aktualisieren des Mitglieds');
    }
  };

  const handleViewProfile = (member) => {
    setSelectedMember(member);
    setShowMemberProfile(true);
  };

  const handleDeleteMember = async (member) => {
    if (window.confirm(`Möchten Sie das Mitglied "${member.first_name} ${member.last_name}" wirklich löschen?`)) {
      try {
        const response = await userAPI.deleteUser(member.id);
        if (response.success) {
          toast.success('Mitglied wurde gelöscht');
          await loadMembers();
        }
      } catch (error) {
        console.error('Fehler beim Löschen des Mitglieds:', error);
        toast.error('Fehler beim Löschen des Mitglieds');
      }
    }
  };

  const handlePromoteToAdmin = async (member) => {
    if (window.confirm(`Möchten Sie "${member.first_name} ${member.last_name}" zum Administrator befördern?`)) {
      try {
        const response = await userAPI.promoteToAdmin(member.id);
        if (response.success) {
          toast.success(`${member.first_name} ${member.last_name} wurde zum Administrator befördert!`);
          setShowEditMember(false);
          setSelectedMember(null);
          await loadMembers();
        }
      } catch (error) {
        console.error('Fehler beim Befördern zum Administrator:', error);
        toast.error('Fehler beim Befördern zum Administrator');
      }
    }
  };

  const exportMembersToExcel = () => {
    // Daten für Excel vorbereiten
    const excelData = members.map((member, index) => ({
      'Nr.': index + 1,
      'Vorname': member.first_name,
      'Nachname': member.last_name,
      'E-Mail': member.email,
      'Username': member.username,
      'Rolle': member.role === 'admin' ? 'Administrator' : 'Mitglied'
    }));

    // Worksheet erstellen
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Column Widths setzen
    worksheet['!cols'] = [
      { wch: 5 },  // Nr.
      { wch: 15 }, // Vorname
      { wch: 15 }, // Nachname
      { wch: 30 }, // E-Mail
      { wch: 15 }, // Username
      { wch: 15 }  // Rolle
    ];

    // Workbook erstellen
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Mitglieder');

    // Datei speichern
    const fileName = `EFC_Mitgliederliste_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    toast.success('Mitgliederliste wurde als Excel exportiert!');
  };

  // useEffect für initialen Load der Mitglieder
  useEffect(() => {
    if (activeSection === 'members') {
      loadMembers();
    }
  }, [activeSection]);

  return (
    <div className="users-tab">
      <div className="users-header">
        <h2>Benutzer-Verwaltung</h2>
      </div>

      {/* Sub-Tabs */}
      <div className="admin-tabs">
        <button
          className={`tab ${activeSection === 'members' ? 'active' : ''}`}
          onClick={() => {
            setActiveSection('members');
            loadMembers();
          }}
        >
          <span className="icon">👤</span>
          Mitglieder
        </button>
        <button
          className={`tab ${activeSection === 'pending' ? 'active' : ''}`}
          onClick={() => {
            setActiveSection('pending');
            if (loadPendingUsers) loadPendingUsers();
          }}
        >
          <span className="icon">⏳</span>
          Freischaltungen{pendingUsers.length > 0 ? ` (${pendingUsers.length})` : ''}
        </button>
      </div>

      {/* Mitglieder-Bereich */}
      {activeSection === 'members' && (
        <div className="members-section">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px'}}>
            <h3>EFC Mitglieder ({members.length})</h3>
            <div style={{display: 'flex', gap: '10px'}}>
              <button
                onClick={exportMembersToExcel}
                className="btn btn-success"
                disabled={members.length === 0}
              >
                📊 Excel Export
              </button>
              <button
                onClick={() => setShowAddMember(true)}
                className="btn btn-primary"
              >
                + Mitglied hinzufügen
              </button>
            </div>
          </div>

          {members.length === 0 ? (
            <div className="empty-state">
              <p>Noch keine Mitglieder vorhanden.</p>
            </div>
          ) : (
            <>
              {/* Desktop Tabelle */}
              <div className="games-table-wrapper">
                <table className="games-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>E-Mail</th>
                      <th>Username</th>
                      <th>Rolle</th>
                      <th>Zugang</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member, index) => {
                      console.log(`Member ${index}:`, member); // Debug einzelne Member
                      return (
                        <tr key={member.id}>
                          <td>{index + 1}</td>
                          <td className="opponent-cell">
                            <strong>{member.first_name} {member.last_name}</strong>
                          </td>
                          <td>{member.email}</td>
                          <td>{member.username}</td>
                          <td className="category-cell">
                            <span className={`category-badge ${member.role === 'admin' ? 'champions-league' : 'bundesliga'}`}>
                              {member.role === 'admin' ? 'Administrator' : 'Mitglied'}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge status-${member.status === 'gesperrt' ? 'past' : 'open'}`}>
                              {member.status === 'gesperrt' ? 'Gesperrt' : 'Aktiv'}
                            </span>
                          </td>
                          <td className="actions-cell">
                            <div className="action-buttons">
                              <button 
                                className="btn btn-sm btn-secondary" 
                                title="Bearbeiten"
                                onClick={() => handleEditMember(member)}
                              >
                                ✏️
                              </button>
                              <button
                                className={`btn btn-sm ${member.status === 'gesperrt' ? 'btn-success' : 'btn-secondary'}`}
                                title={member.status === 'gesperrt' ? 'Sperre aufheben' : 'Zugang sperren'}
                                onClick={() => handleToggleBlock(member)}
                                disabled={member.role === 'admin' && member.status !== 'gesperrt'}
                              >
                                {member.status === 'gesperrt' ? '🔓' : '🔒'}
                              </button>
                              <button 
                                className="btn btn-sm btn-primary" 
                                title="Profil anzeigen"
                                onClick={() => handleViewProfile(member)}
                              >
                                👁️
                              </button>
                              <button 
                                className="btn btn-sm btn-danger" 
                                title="Löschen"
                                onClick={() => handleDeleteMember(member)}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="users-mobile-grid">
                {members.map((member, index) => (
                  <div key={member.id} className="user-mobile-card">
                    <div className="user-mobile-header">
                      <div>
                        <h3 className="user-mobile-name">
                          {member.first_name} {member.last_name}
                        </h3>
                        <span className={`category-badge user-mobile-role ${member.role === 'admin' ? 'champions-league' : 'bundesliga'}`}>
                          {member.role === 'admin' ? 'Administrator' : 'Mitglied'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="user-mobile-info">
                      <div>
                        <strong>Username:</strong> {member.username}
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <strong>E-Mail:</strong> {member.email}
                      </div>
                    </div>
                    
                    <div className="user-mobile-actions">
                      <button 
                        className="btn btn-sm btn-secondary" 
                        title="Bearbeiten"
                        onClick={() => handleEditMember(member)}
                      >
                        ✏️
                      </button>
                      <button
                        className={`btn btn-sm ${member.status === 'gesperrt' ? 'btn-success' : 'btn-secondary'}`}
                        title={member.status === 'gesperrt' ? 'Sperre aufheben' : 'Zugang sperren'}
                        onClick={() => handleToggleBlock(member)}
                        disabled={member.role === 'admin' && member.status !== 'gesperrt'}
                      >
                        {member.status === 'gesperrt' ? '🔓' : '🔒'}
                      </button>
                      <button 
                        className="btn btn-sm btn-primary" 
                        title="Profil anzeigen"
                        onClick={() => handleViewProfile(member)}
                      >
                        👁️
                      </button>
                      <button 
                        className="btn btn-sm btn-danger" 
                        title="Löschen"
                        onClick={() => handleDeleteMember(member)}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {activeSection === 'pending' && (
        <div className="members-section">
          <h3>Wartende Freischaltungen ({pendingUsers.length})</h3>
          <p style={{color: '#666', fontSize: '0.9em', marginBottom: '20px'}}>
            Diese Konten haben ihre E-Mail-Adresse bestätigt und warten auf die Freigabe
            durch den Vorstand. Vorher ist keine Anmeldung möglich.
          </p>

          {pendingUsers.length === 0 ? (
            <div className="empty-state">
              <p>✅ Zurzeit wartet niemand auf Freischaltung.</p>
            </div>
          ) : (
            <div className="games-grid">
              {pendingUsers.map((user) => (
                <div key={user.id} className="game-card">
                  <div className="card-header">
                    <span className="status-badge almost-full">Wartet</span>
                  </div>
                  <div className="game-info">
                    <h3>{user.first_name} {user.last_name}</h3>
                    <p className="game-location">📧 {user.email}</p>
                    <p className="game-location">👤 {user.username}</p>
                    <p className="game-date">
                      Registriert: {new Date(user.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  <div className="game-actions">
                    <button
                      onClick={() => handleActivateUser(user)}
                      className="btn btn-success btn-sm"
                      disabled={activatingUser === user.id}
                      style={{width: '100%'}}
                    >
                      {activatingUser === user.id ? 'Schalte frei …' : '✅ Freischalten'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mitglied hinzufügen Modal */}
      {showAddMember && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Neues Mitglied hinzufügen</h2>
              <button
                onClick={() => setShowAddMember(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddMember} className="game-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">Vorname *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={memberForm.firstName}
                    onChange={handleMemberFormChange}
                    required
                    placeholder="z.B. Max"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Nachname *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={memberForm.lastName}
                    onChange={handleMemberFormChange}
                    required
                    placeholder="z.B. Mustermann"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">E-Mail *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={memberForm.email}
                    onChange={handleMemberFormChange}
                    required
                    placeholder="z.B. max@example.com"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="username">Username *</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={memberForm.username}
                    onChange={handleMemberFormChange}
                    required
                    placeholder="z.B. maxmustermann"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Passwort *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={memberForm.password}
                    onChange={handleMemberFormChange}
                    required
                    placeholder="Mindestens 6 Zeichen"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowAddMember(false)}
                  className="btn btn-secondary"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Mitglied erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mitglied bearbeiten Modal */}
      {showEditMember && selectedMember && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Mitglied bearbeiten</h2>
              <button
                onClick={() => setShowEditMember(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateMember} className="game-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="editFirstName">Vorname *</label>
                  <input
                    type="text"
                    id="editFirstName"
                    name="firstName"
                    value={memberForm.firstName}
                    onChange={handleMemberFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editLastName">Nachname *</label>
                  <input
                    type="text"
                    id="editLastName"
                    name="lastName"
                    value={memberForm.lastName}
                    onChange={handleMemberFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="editEmail">E-Mail *</label>
                  <input
                    type="email"
                    id="editEmail"
                    name="email"
                    value={memberForm.email}
                    onChange={handleMemberFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editUsername">Username *</label>
                  <input
                    type="text"
                    id="editUsername"
                    name="username"
                    value={memberForm.username}
                    onChange={handleMemberFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="editPassword">Neues Passwort (optional)</label>
                  <input
                    type="password"
                    id="editPassword"
                    name="password"
                    value={memberForm.password}
                    onChange={handleMemberFormChange}
                    placeholder="Leer lassen um nicht zu ändern"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowEditMember(false)}
                  className="btn btn-secondary"
                >
                  Abbrechen
                </button>
                {selectedMember.role !== 'admin' && (
                  <button
                    type="button"
                    onClick={() => handlePromoteToAdmin(selectedMember)}
                    className="btn btn-success"
                  >
                    👑 Zu Administrator befördern
                  </button>
                )}
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Änderungen speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Profil anzeigen Modal */}
      {showMemberProfile && selectedMember && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h2>Mitgliedsprofil - {selectedMember.first_name} {selectedMember.last_name}</h2>
              <button
                onClick={() => setShowMemberProfile(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>

            <div className="profile-content">
              <div className="profile-section">
                <h3>Persönliche Daten</h3>
                <div className="profile-grid">
                  <div className="profile-item">
                    <strong>Vorname:</strong>
                    <span>{selectedMember.first_name}</span>
                  </div>
                  <div className="profile-item">
                    <strong>Nachname:</strong>
                    <span>{selectedMember.last_name}</span>
                  </div>
                  <div className="profile-item">
                    <strong>E-Mail:</strong>
                    <span>{selectedMember.email}</span>
                  </div>
                  <div className="profile-item">
                    <strong>Username:</strong>
                    <span>{selectedMember.username}</span>
                  </div>
                  <div className="profile-item">
                    <strong>Rolle:</strong>
                    <span className={`category-badge ${selectedMember.role === 'admin' ? 'champions-league' : 'bundesliga'}`}>
                      {selectedMember.role === 'admin' ? 'Administrator' : 'Mitglied'}
                    </span>
                  </div>
                  <div className="profile-item">
                    <strong>E-Mail verifiziert:</strong>
                    <span className={`status-badge status-${selectedMember.email_verified ? 'open' : 'past'}`}>
                      {selectedMember.email_verified ? 'Ja' : 'Nein'}
                    </span>
                  </div>
                  <div className="profile-item">
                    <strong>Registriert am:</strong>
                    <span>{new Date(selectedMember.created_at).toLocaleDateString('de-DE')}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowMemberProfile(false);
                  handleEditMember(selectedMember);
                }}
                className="btn btn-primary"
              >
                Bearbeiten
              </button>
              <button
                onClick={() => setShowMemberProfile(false)}
                className="btn btn-secondary"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersTab;
